import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { useTranslation } from 'react-i18next';

import { useAppContext } from '../AppContext';

function CreateNexusPersonaForm({ nexus, onPersonaCreated }) {
    const { t } = useTranslation();
    const { appFunctions, setMessage, showConfirmation, LucideIcons, uploadFile } = useAppContext();
    const [name, setName] = useState('');
    const [bioPrompt, setBioPrompt] = useState('');
    const [interests, setInterests] = useState('');
    const [avatarPrompt, setAvatarPrompt] = useState('');
    const [avatarFile, setAvatarFile] = useState(null);
    const [avatarPreview, setAvatarPreview] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const PERSONA_CREATION_COST = 250;

    const handleAvatarChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setAvatarFile(file);
            setAvatarPreview(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        showConfirmation({
            message: t('createNexusPersona.confirmation', { nexusName: nexus.name, cost: PERSONA_CREATION_COST }),
            onConfirm: async () => {
                setIsSubmitting(true);
                let photoURL = `https://api.dicebear.com/7.x/bottts-neutral/svg?seed=${encodeURIComponent(avatarPrompt || name)}`;

                try {
                    if (avatarFile) {
                        const tempId = `ai-${nexus.id.slice(0, 5)}-${Date.now()}`;
                        const filePath = `avatars/${tempId}/${avatarFile.name}`;
                        photoURL = await uploadFile(avatarFile, filePath);
                    }

                    const createNexusPersona = httpsCallable(appFunctions, 'createNexusPersona');
                    await createNexusPersona({
                        nexusId: nexus.id,
                        name,
                        bio_prompt: bioPrompt,
                        interests_list: interests.split(',').map(i => i.trim()).filter(Boolean),
                        avatar_prompt: avatarPrompt,
                        photoURL: photoURL
                    });
                    setMessage(t('createNexusPersona.success'));
                    onPersonaCreated();
                } catch (error) {
                    setMessage(`${t('createNexusPersona.failure')}: ${error.message}`);
                } finally {
                    setIsSubmitting(false);
                }
            }
        });
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4 max-w-lg mx-auto animate-fadeIn">
            <h4 className="text-lg font-bold text-center text-purple-200">{t('createNexusPersona.title')} <span className="text-white">{nexus.name}</span></h4>
            
            <div className="flex flex-col items-center gap-4">
                <img src={avatarPreview || `https://api.dicebear.com/7.x/bottts-neutral/svg?seed=${encodeURIComponent(avatarPrompt || name)}`} alt={t('createNexusPersona.avatarPreview')} className="w-24 h-24 rounded-full object-cover bg-gray-700" />
                <input type="file" onChange={handleAvatarChange} className="text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-purple-500 file:text-white hover:file:bg-purple-600" accept="image/png, image/jpeg, image/gif" />
            </div>

            <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder={t('createNexusPersona.namePlaceholder')} className="w-full bg-gray-800 p-3 rounded-lg border border-gray-700" required />
            <textarea value={bioPrompt} onChange={e => setBioPrompt(e.target.value)} placeholder={t('createNexusPersona.bioPlaceholder')} className="w-full bg-gray-800 p-3 rounded-lg border border-gray-700 h-24" required />
            <input type="text" value={interests} onChange={e => setInterests(e.target.value)} placeholder={t('createNexusPersona.interestsPlaceholder')} className="w-full bg-gray-800 p-3 rounded-lg border border-gray-700" required />
            <input type="text" value={avatarPrompt} onChange={e => setAvatarPrompt(e.target.value)} placeholder={t('createNexusPersona.avatarPlaceholder')} className="w-full bg-gray-800 p-3 rounded-lg border border-gray-700" />
            
            <button type="submit" disabled={isSubmitting} className="w-full small-action-button bg-purple-600 hover:bg-purple-700 text-lg py-3 disabled:opacity-50">
                {isSubmitting ? t('createNexusPersona.forging') : t('createNexusPersona.forgeButton', { cost: PERSONA_CREATION_COST })}
            </button>
        </form>
    );
}

export default CreateNexusPersonaForm;